import { useEffect, useMemo, useState } from 'react'
import { setupDataMessageHandler } from '@livekit/components-core'
import { useMaybeRoomContext } from '@livekit/components-react'

const TELEMETRY_TOPIC = 'telemetry.location'
/**
 * The watch screen's people (`u_<user>.<tab>`, `anon_<uuid>`): a live host may publish data (their map's view), so a
 * location packet from one is forged. Cameras' points come from the API (no participant) or a camera's own connection.
 */
const PERSON_IDENTITY = /^(u_|anon_)/
const DEFAULT_STALE_THRESHOLD_MS = 3000
const decoder = new TextDecoder()

export interface StreamTelemetryPoint {
  streamId: string
  eventId: string
  latitude: number
  longitude: number
  headingDeg?: number
  speedMps?: number
  accuracyM?: number
  timestamp: string
  receivedAt: number
}

type TelemetryMap = Record<string, StreamTelemetryPoint>

export function useStreamTelemetry(
  eventId?: string | null,
  options: { staleThresholdMs?: number } = {},
) {
  const room = useMaybeRoomContext()
  const [points, setPoints] = useState<TelemetryMap>({})
  const staleThresholdMs = options.staleThresholdMs ?? DEFAULT_STALE_THRESHOLD_MS

  useEffect(() => {
    if (!room) {
      setPoints({})
      return
    }

    const { messageObservable } = setupDataMessageHandler(room, [TELEMETRY_TOPIC])
    const subscription = messageObservable.subscribe((message) => {
      if (!message.payload?.length || PERSON_IDENTITY.test(message.from?.identity ?? '')) {
        return
      }

      const decoded = decodePayload(message.payload)

      if (!decoded) {
        return
      }

      if (eventId && decoded.eventId !== eventId) {
        return
      }

      setPoints((prev) => ({
        ...prev,
        [decoded.streamId]: decoded,
      }))
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [room, eventId])

  useEffect(() => {
    const interval = window.setInterval(() => {
      setPoints((prev) => {
        const now = Date.now()
        let mutated = false
        const next: TelemetryMap = { ...prev }

        for (const [streamId, point] of Object.entries(prev)) {
          if (now - point.receivedAt > staleThresholdMs) {
            mutated = true
            delete next[streamId]
          }
        }

        return mutated ? next : prev
      })
    }, 1000)

    return () => window.clearInterval(interval)
  }, [staleThresholdMs])

  return useMemo(() => Object.values(points), [points])
}

type RawTelemetryPayload = {
  event_id?: string
  eventId?: string
  stream_id?: string
  streamId?: string
  latitude?: number
  lat?: number
  longitude?: number
  lng?: number
  heading_deg?: number
  headingDeg?: number
  heading?: number
  speed_mps?: number
  speedMps?: number
  speed?: number
  accuracy_m?: number
  accuracyM?: number
  accuracy?: number
  timestamp?: string
  source?: string
}

function decodePayload(buffer: Uint8Array): StreamTelemetryPoint | null {
  try {
    const text = decoder.decode(buffer)
    const payload = JSON.parse(text) as RawTelemetryPayload

    const streamId = payload.stream_id ?? payload.streamId
    const eventId = payload.event_id ?? payload.eventId
    const lat = Number(payload.latitude ?? payload.lat)
    const lng = Number(payload.longitude ?? payload.lng)

    if (!streamId || !eventId || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null
    }

    const heading = payload.heading_deg ?? payload.headingDeg ?? payload.heading
    const speed = payload.speed_mps ?? payload.speedMps ?? payload.speed
    const accuracy = payload.accuracy_m ?? payload.accuracyM ?? payload.accuracy
    const timestamp = payload.timestamp ?? new Date().toISOString()

    return {
      streamId,
      eventId,
      latitude: lat,
      longitude: lng,
      headingDeg: typeof heading === 'number' ? heading : undefined,
      speedMps: typeof speed === 'number' ? speed : undefined,
      accuracyM: typeof accuracy === 'number' ? accuracy : undefined,
      timestamp,
      receivedAt: Date.now(),
    }
  } catch {
    return null
  }
}
